/**
 * HierarchyLadder
 * Renders the 2→4→6→8 tariff prediction as a ladder: one rung per level with
 * its code, title, a confidence bar, and (when feedback is enabled) a per-rung
 * "Correct" button. The confidence per rung is the share of retrieval mass that
 * agreed on that chapter/heading/subheading — so a rung can be highly confident
 * even when the exact 8-digit leaf is not.
 */
import React from 'react';
import { Box, Typography, LinearProgress, Button, Tooltip, Chip } from '@mui/material';
import { Flag as FlagIcon } from '@mui/icons-material';
import { TariffHierarchy, TariffHierarchyLevel } from '../../../services/tariffClassifierPageService';
import { brand } from '../../../theme';

interface HierarchyLadderProps {
  hierarchy: TariffHierarchy;
  hasFeedback?: boolean;
  onCorrect?: (level: TariffHierarchyLevel) => void;
}

const LEVEL_LABELS: Record<string, string> = {
  chapter: 'Chapter',
  heading: 'Heading',
  subheading: 'Subheading',
  cn8: 'Full code',
};

// Consistent dot-grouped pairs at every level, matching the headline HS-code
// display (e.g. 84 · 84.71 · 84.71.30 · 84.71.30.00).
const formatCode = (code: string): string => {
  const c = (code || '').replace(/[\s.-]/g, '');
  return (c.match(/.{1,2}/g) || [c]).join('.');
};

// De-shout ALL-CAPS titles (chapters) to sentence case, and capitalise the
// first letter of lowercase keyword titles (some scraped headings); leave
// already mixed-case tariff text untouched.
const prettifyTitle = (s: string | null): string => {
  if (!s) return '—';
  if (/[A-Z]/.test(s) && s === s.toUpperCase()) {
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
  }
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const HierarchyLadder: React.FC<HierarchyLadderProps> = ({ hierarchy, hasFeedback, onCorrect }) => {
  const levels = hierarchy?.levels ?? [];
  if (!levels.length) return null;

  return (
    <Box sx={{ mb: 2.5 }}>
      <Box sx={{ mb: 1 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight={700}
          letterSpacing={1}
          sx={{ textTransform: 'uppercase', display: 'block' }}
        >
          Level breakdown
        </Typography>
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
          Each bar shows how much the retrieved candidates agree at that level — not the overall match score.
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {levels.map((lv, i) => {
          const pct = Math.round((lv.confidence || 0) * 100);
          const isLeaf = i === levels.length - 1;
          const title = prettifyTitle(lv.title);
          return (
            <Box
              key={lv.level}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                py: 1,
                px: 1.25,
                // Nesting: growing indent + a left accent that reads as a tree.
                // The leaf (final answer) picks up the headline's orange accent.
                ml: i * 1.5,
                borderLeft: '3px solid',
                borderLeftColor: isLeaf ? brand.orange : 'divider',
                borderRadius: '0 8px 8px 0',
                bgcolor: isLeaf ? 'rgba(255,152,0,0.06)' : 'transparent',
                transition: 'background-color .15s ease',
                '&:hover': { bgcolor: isLeaf ? 'rgba(255,152,0,0.10)' : 'action.hover' },
                '&:hover .rung-correct': { opacity: 1 },
              }}
            >
              <Chip
                label={LEVEL_LABELS[lv.level] || lv.level}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.68rem', height: 20, minWidth: 82, fontWeight: 600 }}
              />
              <Typography
                sx={{
                  fontFamily: 'monospace',
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  minWidth: 108,
                  color: isLeaf ? brand.orange : brand.textPrimary,
                }}
              >
                {formatCode(lv.code)}
              </Typography>
              <Tooltip title={title} arrow>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {title}
                </Typography>
              </Tooltip>

              {/* Agreement bar + pct — single neutral tone; a low share is
                  normal here, not an error, so no traffic-light colouring. */}
              <Box sx={{ width: 96, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <LinearProgress
                  variant="determinate"
                  value={pct}
                  sx={{
                    flex: 1,
                    height: 6,
                    borderRadius: 3,
                    bgcolor: 'grey.200',
                    '& .MuiLinearProgress-bar': { backgroundColor: brand.orange },
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 30, textAlign: 'right' }}>
                  {pct}%
                </Typography>
              </Box>

              {hasFeedback && onCorrect && (
                <Tooltip title={`Correct the ${LEVEL_LABELS[lv.level]?.toLowerCase() || lv.level}`} arrow>
                  <Button
                    className="rung-correct"
                    size="small"
                    variant="text"
                    color="warning"
                    startIcon={<FlagIcon sx={{ fontSize: '0.9rem' }} />}
                    onClick={() => onCorrect(lv)}
                    sx={{
                      textTransform: 'none',
                      fontSize: '0.72rem',
                      minWidth: 0,
                      px: 1,
                      // Subtle until the row is hovered, to declutter the tree.
                      opacity: 0.4,
                      transition: 'opacity .15s ease',
                    }}
                  >
                    Correct?
                  </Button>
                </Tooltip>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default HierarchyLadder;
